/**
 * Cleo ERP SaaS — Détail abonnement billing admin
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Tag, Button, Descriptions, Space,
  Spin, Alert, Divider, Modal, message, Timeline, Badge,
} from 'antd';
import {
  ArrowLeftOutlined, StopOutlined, ExclamationCircleFilled,
  LoadingOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import api from '../../../api/axios';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  ACTIVE: '#10B981', TRIAL: '#3B82F6', PAST_DUE: '#F59E0B',
  SUSPENDED: '#64748B', CANCELLATION_PENDING: '#F97316', CANCELLED: '#EF4444',
};
const STATUS_LABELS = {
  ACTIVE: 'Actif', TRIAL: "Période d'essai", PAST_DUE: 'Paiement en retard',
  SUSPENDED: 'Suspendu', CANCELLATION_PENDING: 'Annulation programmée', CANCELLED: 'Annulé',
};
const CANCEL_STATUS_COLORS = {
  PENDING: '#F59E0B', ACKNOWLEDGED: '#3B82F6',
  PROCESSED: '#10B981', REJECTED: '#EF4444', WITHDRAWN: '#94A3B8',
};
const CANCEL_STATUS_LABELS = {
  PENDING: 'En attente', ACKNOWLEDGED: 'Prise en compte',
  PROCESSED: 'Traitée', REJECTED: 'Rejetée', WITHDRAWN: 'Retirée',
};

function BillingSubscriptionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.get(`/api/admin/billing/subscriptions/${id}/`);
      setSub(res.data);
    } catch {
      setError('Abonnement introuvable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleConfirmCancellation = () => {
    Modal.confirm({
      title: "Confirmer l'annulation de l'abonnement",
      icon: <ExclamationCircleFilled style={{ color: '#F97316' }} />,
      content: (
        <div>
          <p>L'abonnement de <strong>{sub.tenant_slug}</strong> sera annulé sur Paddle.</p>
          <p>L'accès restera actif jusqu'à la prochaine date de facturation (<strong>{sub.next_billing_date}</strong>).</p>
          <p>Cette action est irréversible.</p>
        </div>
      ),
      okText: "Confirmer l'annulation",
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await api.post(`/api/admin/billing/subscriptions/${id}/confirm-cancellation/`);
          message.success(res.data.detail || 'Annulation confirmée sur Paddle.');
          fetchData();
        } catch (err) {
          message.error(err.response?.data?.detail || "Erreur lors de l'annulation.");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#10B981' }} />} />
    </div>
  );

  if (error) return <Alert message={error} type="error" showIcon />;
  if (!sub) return null;

  const pendingCancellation = sub.cancellation_requests?.find(
    (r) => r.status === 'PENDING' || r.status === 'ACKNOWLEDGED'
  );

  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/admin/billing/subscriptions')}
        style={{ marginBottom: 16, color: '#64748B' }}
      >
        Retour aux abonnements
      </Button>

      {/* En-tête */}
      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#0F172A' }}>{sub.tenant_slug}</Title>
            <Text style={{ color: '#64748B' }}>{sub.company_name} — {sub.country_code}</Text>
          </div>
          <Space>
            <Tag color={STATUS_COLORS[sub.status]} style={{ fontSize: 14, padding: '4px 16px', borderRadius: 6 }}>
              {STATUS_LABELS[sub.status] || sub.status}
            </Tag>
          </Space>
        </div>

        <Descriptions column={{ xs: 1, sm: 2 }} size="small"
          labelStyle={{ color: '#64748B', fontWeight: 500, fontSize: 13 }}
          contentStyle={{ color: '#0F172A', fontSize: 13 }}>
          <Descriptions.Item label="Plan">{sub.plan_name}</Descriptions.Item>
          <Descriptions.Item label="Email admin">{sub.admin_email}</Descriptions.Item>
          <Descriptions.Item label="Montant mensuel">
            <Text strong style={{ color: '#10B981' }}>
              {parseFloat(sub.monthly_cost).toLocaleString('fr-FR')} {sub.currency}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Prochaine facturation">
            {sub.next_billing_date || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Début abonnement">
            {sub.started_at ? new Date(sub.started_at).toLocaleDateString('fr-FR') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Échecs paiement">
            {sub.payment_failure_count > 0
              ? <Tag color="#EF4444">{sub.payment_failure_count}</Tag>
              : '0'}
          </Descriptions.Item>
          {sub.paddle_subscription_id && (
            <Descriptions.Item label="Paddle Subscription ID">
              <Text copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>
                {sub.paddle_subscription_id}
              </Text>
            </Descriptions.Item>
          )}
          {sub.paddle_customer_id && (
            <Descriptions.Item label="Paddle Customer ID">
              <Text copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>
                {sub.paddle_customer_id}
              </Text>
            </Descriptions.Item>
          )}
          {sub.payment_method?.card_last4 && (
            <Descriptions.Item label="Moyen de paiement">
              {sub.payment_method.card_type} ****{sub.payment_method.card_last4}
              {sub.payment_method.card_exp_month && (
                <Text style={{ color: '#64748B', marginLeft: 8 }}>
                  expire {sub.payment_method.card_exp_month}/{sub.payment_method.card_exp_year}
                </Text>
              )}
            </Descriptions.Item>
          )}
          {sub.cancellation_requested_at && (
            <Descriptions.Item label="Annulation demandée le">
              <Text style={{ color: '#F97316' }}>
                {new Date(sub.cancellation_requested_at).toLocaleString('fr-FR')}
              </Text>
            </Descriptions.Item>
          )}
          {sub.cancellation_effective_date && (
            <Descriptions.Item label="Date d'effet annulation">
              {new Date(sub.cancellation_effective_date).toLocaleDateString('fr-FR')}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider style={{ margin: '20px 0' }} />

        <Space wrap>
          {(sub.status === 'CANCELLATION_PENDING' || (sub.status === 'ACTIVE' && pendingCancellation)) && (
            <Button
              danger
              icon={<StopOutlined />}
              loading={actionLoading}
              onClick={handleConfirmCancellation}
            >
              Confirmer l'annulation sur Paddle
            </Button>
          )}
        </Space>
      </Card>

      {/* Demandes d'annulation */}
      {sub.cancellation_requests?.length > 0 && (
        <Card
          title={<Title level={5} style={{ margin: 0 }}>Demandes d'annulation</Title>}
          style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 24 }}
        >
          <Timeline
            items={sub.cancellation_requests.map((req) => ({
              color: CANCEL_STATUS_COLORS[req.status] || '#CBD5E1',
              dot: req.status === 'PROCESSED'
                ? <CheckCircleOutlined style={{ color: '#10B981' }} />
                : <ClockCircleOutlined style={{ color: CANCEL_STATUS_COLORS[req.status] }} />,
              children: (
                <div style={{ marginBottom: 8 }}>
                  <Space>
                    <Text strong style={{ color: '#0F172A' }}>
                      {req.reason_display}
                    </Text>
                    <Tag color={CANCEL_STATUS_COLORS[req.status]} style={{ borderRadius: 6 }}>
                      {CANCEL_STATUS_LABELS[req.status] || req.status}
                    </Tag>
                  </Space>
                  <div>
                    <Text style={{ color: '#64748B', fontSize: 12 }}>
                      Demandée par {req.requested_by_email} le {new Date(req.requested_at).toLocaleString('fr-FR')}
                    </Text>
                  </div>
                  {req.requested_effective_date && (
                    <div>
                      <Text style={{ color: '#64748B', fontSize: 12 }}>
                        Date d'effet souhaitée : {new Date(req.requested_effective_date).toLocaleDateString('fr-FR')}
                      </Text>
                    </div>
                  )}
                  {req.comment && (
                    <div>
                      <Text style={{ color: '#94A3B8', fontSize: 12 }}>"{req.comment}"</Text>
                    </div>
                  )}
                  {req.internal_notes && (
                    <div style={{ marginTop: 4, background: '#F8FAFC', padding: '6px 10px', borderRadius: 6 }}>
                      <Text style={{ color: '#64748B', fontSize: 12 }}>{req.internal_notes}</Text>
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        </Card>
      )}
    </div>
  );
}

export default BillingSubscriptionDetailPage;
